import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { useLocation } from "react-router-dom";
import Avatar from "../../Avatar";
import Button from "../../Button";
import Flex from "../../Flex";
import Icon from "../../Icon";
import Popover from "../../Popover";
import { H6 } from "../../Typography";
import TeamTypeBar, { TeamTypeBarPlaceholder } from "../components/TeamTypeBar";
import { PinnedItemShape } from "../types/sideNav";
import {
  SectionItem,
  SectionItemList,
  SectionItemName,
  SectionPinnedLink,
  SectionPinnedLinkContent,
  SectionResponsiveContainer,
  CollapsedAvatarWrapper,
  CollapsedItemLink,
  CollapsedItemWrapper,
  UnpinButtonWrapper,
} from "../styles/sideNavSection.styles";
import {
  getDropIndexFromPointer,
  toSpliceInsertIndex,
  wouldReorderChangeOrder,
} from "../utils/pinnedReorderUtils";

const PINNED_ITEM_DRAG_TYPE = "side-nav-pinned-item";
const TRANSPARENT_DRAG_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const PinnedSectionHeader = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: ${(props) => themeGet("space.xs")(props)};
`;

const EditModeButton = styled(Button)`
  flex-shrink: 0;
`;

const DragHandle = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== "isDragging",
})`
  align-self: center;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 14px;
  min-height: 24px;
  padding: 0;
  margin: 0;
  background: transparent;
  border: none;
  cursor: ${(props) => (props.isDragging ? "grabbing" : "grab")};
  user-select: none;
  touch-action: none;

  &:hover,
  &:focus,
  &:active {
    background: transparent;
  }
`;

const DragHandleGrip = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3px;
  width: 12px;
`;

const DragHandleLine = styled.span`
  display: block;
  height: 2px;
  width: 12px;
  border-radius: 999px;
  background-color: ${(props) => themeGet("colors.greyDark")(props)};
  opacity: 0.55;
`;

const ReorderableSectionItem = styled(SectionItem).withConfig({
  shouldForwardProp: (prop) =>
    !["isDragging", "isEditMode", "showDropIndicator"].includes(prop),
})`
  position: relative;

  ${(props) =>
    props.isDragging &&
    `
    opacity: 0.65;
  `}

  ${(props) =>
    props.isEditMode &&
    `
    &:hover {
      background-color: transparent;
    }
  `}

  ${(props) =>
    props.showDropIndicator &&
    `
    &::before {
      content: "";
      position: absolute;
      top: -2px;
      left: 0;
      right: 0;
      height: 2px;
      border-radius: 1px;
      background-color: ${themeGet("colors.linkText")(props)};
      pointer-events: none;
      z-index: 1;
    }
  `}
`;

const PinnedDropTarget = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${(props) => themeGet("space.r")(props)};
`;

const PinnedSectionItemList = styled(SectionItemList).withConfig({
  shouldForwardProp: (prop) => prop !== "isEditModeDragging",
})`
  ${(props) =>
    props.isEditModeDragging &&
    `
    padding-bottom: 20px;
  `}
`;

const TrailingDropZone = styled.li.withConfig({
  shouldForwardProp: (prop) => prop !== "showDropIndicator",
})`
  list-style: none;
  width: 100%;
  min-height: 36px;
  margin: 0;
  padding: 4px 0 0;
  flex-shrink: 0;
  position: relative;
  pointer-events: auto;

  ${(props) =>
    props.showDropIndicator &&
    `
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      border-radius: 1px;
      background-color: ${themeGet("colors.linkText")(props)};
      pointer-events: none;
    }
  `}
`;

/**
 * SideNavPinnedSection - "Pinned" section of the side navigation.
 */
const SideNavPinnedSection = ({
  items,
  isExpanded,
  onReorder,
  isEditMode: isEditModeProp,
  onEditModeChange,
}) => {
  const location = useLocation();
  const [isEditModeInternal, setIsEditModeInternal] = useState(false);
  const [dragStartIndex, setDragStartIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDropAfterLast, setIsDropAfterLast] = useState(false);
  const rowRefs = useRef([]);
  const sectionRef = useRef(null);
  const dragStartIndexRef = useRef(null);
  const dragOverIndexRef = useRef(null);
  const isDropAfterLastRef = useRef(false);
  const dropCommittedRef = useRef(false);
  const itemCount = items?.length ?? 0;

  const isControlledEditMode = typeof isEditModeProp === "boolean";
  const isEditMode = isControlledEditMode ? isEditModeProp : isEditModeInternal;
  const setIsEditMode = useCallback(
    (nextValue) => {
      if (isControlledEditMode) {
        onEditModeChange?.(nextValue);
        return;
      }
      setIsEditModeInternal(nextValue);
    },
    [isControlledEditMode, onEditModeChange]
  );

  const canReorder = Boolean(onReorder) && isExpanded;
  const isDragging = dragStartIndex !== null;
  const isReorderUiActive = isEditMode && isExpanded;

  const clearDragState = useCallback(() => {
    dragStartIndexRef.current = null;
    dragOverIndexRef.current = null;
    isDropAfterLastRef.current = false;
    dropCommittedRef.current = false;
    setDragStartIndex(null);
    setDragOverIndex(null);
    setIsDropAfterLast(false);
  }, []);

  useEffect(() => {
    if (!isExpanded && isEditMode) {
      clearDragState();
      setIsEditMode(false);
    }
  }, [clearDragState, isEditMode, isExpanded, setIsEditMode]);

  const resolveDropIndex = useCallback((clientY, itemCount) => {
    const rowElements = rowRefs.current.filter(Boolean);
    return getDropIndexFromPointer(clientY, rowElements, itemCount);
  }, []);

  const setDropTarget = useCallback((nextDropIndex, itemCount) => {
    const dropAfterLast = nextDropIndex >= itemCount;

    dragOverIndexRef.current = dropAfterLast ? itemCount : nextDropIndex;
    isDropAfterLastRef.current = dropAfterLast;

    setDragOverIndex(dragOverIndexRef.current);
    setIsDropAfterLast(dropAfterLast);
  }, []);

  const commitReorder = useCallback(
    (fromIndex, toIndex) => {
      if (
        fromIndex === null ||
        toIndex === null ||
        !onReorder ||
        !wouldReorderChangeOrder(fromIndex, toIndex, itemCount)
      ) {
        return false;
      }

      onReorder(fromIndex, toIndex);
      return true;
    },
    [itemCount, onReorder]
  );

  const finalizeDrag = useCallback(
    (clientY = null) => {
      if (dropCommittedRef.current || dragStartIndexRef.current === null) {
        clearDragState();
        return;
      }

      const fromIndex = dragStartIndexRef.current;
      let insertBeforeIndex = isDropAfterLastRef.current
        ? itemCount
        : dragOverIndexRef.current;

      if (clientY !== null) {
        insertBeforeIndex = resolveDropIndex(clientY, itemCount);
        const dropAfterLast = insertBeforeIndex >= itemCount;
        isDropAfterLastRef.current = dropAfterLast;
        dragOverIndexRef.current = dropAfterLast
          ? itemCount
          : insertBeforeIndex;
      }

      const toIndex =
        insertBeforeIndex === null
          ? fromIndex
          : toSpliceInsertIndex(fromIndex, insertBeforeIndex, itemCount);

      if (
        fromIndex !== null &&
        toIndex !== null &&
        wouldReorderChangeOrder(fromIndex, toIndex, itemCount)
      ) {
        commitReorder(fromIndex, toIndex);
      }

      dropCommittedRef.current = true;
      clearDragState();
    },
    [clearDragState, commitReorder, itemCount, resolveDropIndex]
  );

  const toggleEditMode = useCallback(() => {
    if (isDragging) {
      return;
    }

    if (isEditMode) {
      clearDragState();
    }

    setIsEditMode(!isEditMode);
  }, [clearDragState, isDragging, isEditMode, setIsEditMode]);

  const updateDropTargetFromPointer = useCallback(
    (clientY) => {
      setDropTarget(resolveDropIndex(clientY, itemCount), itemCount);
    },
    [itemCount, resolveDropIndex, setDropTarget]
  );

  useEffect(() => {
    if (!isReorderUiActive || !isDragging) {
      return undefined;
    }

    const handleDocumentDragOver = (event) => {
      if (dragStartIndexRef.current === null) {
        return;
      }

      event.preventDefault();

      const rowElements = rowRefs.current.filter(Boolean);
      if (!rowElements.length) {
        return;
      }

      const lastRowRect =
        rowElements[rowElements.length - 1].getBoundingClientRect();

      if (event.clientY >= lastRowRect.bottom) {
        setDropTarget(itemCount, itemCount);
        return;
      }

      const sectionElement = sectionRef.current;
      if (!sectionElement) {
        return;
      }

      const sectionRect = sectionElement.getBoundingClientRect();
      if (
        event.clientY < sectionRect.top ||
        event.clientY > sectionRect.bottom
      ) {
        return;
      }

      updateDropTargetFromPointer(event.clientY);
    };

    document.addEventListener("dragover", handleDocumentDragOver);
    return () => {
      document.removeEventListener("dragover", handleDocumentDragOver);
    };
  }, [
    isDragging,
    isReorderUiActive,
    itemCount,
    setDropTarget,
    updateDropTargetFromPointer,
  ]);

  const handleDragStart = useCallback((event, index) => {
    dropCommittedRef.current = false;
    dragStartIndexRef.current = index;
    dragOverIndexRef.current = index;
    isDropAfterLastRef.current = false;

    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(PINNED_ITEM_DRAG_TYPE, String(index));

    const dragImage = new Image();
    dragImage.src = TRANSPARENT_DRAG_IMAGE;
    event.dataTransfer.setDragImage(dragImage, 0, 0);

    setDragStartIndex(index);
    setDragOverIndex(index);
    setIsDropAfterLast(false);
  }, []);

  const handleDragOver = useCallback(
    (event) => {
      if (
        !isReorderUiActive ||
        !onReorder ||
        dragStartIndexRef.current === null
      ) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      updateDropTargetFromPointer(event.clientY);
    },
    [isReorderUiActive, onReorder, updateDropTargetFromPointer]
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (dragStartIndexRef.current === null || !onReorder) {
        clearDragState();
        return;
      }

      finalizeDrag(event.clientY);
    },
    [clearDragState, finalizeDrag, onReorder]
  );

  const handleTrailingDragOver = useCallback(
    (event) => {
      if (
        !isReorderUiActive ||
        !onReorder ||
        dragStartIndexRef.current === null
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      setDropTarget(itemCount, itemCount);
    },
    [isReorderUiActive, itemCount, onReorder, setDropTarget]
  );

  const handleKeyboardMove = useCallback(
    (fromIndex, direction) => {
      if (!onReorder || !isReorderUiActive) {
        return;
      }

      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= itemCount) {
        return;
      }

      commitReorder(fromIndex, toIndex);
    },
    [commitReorder, isReorderUiActive, itemCount, onReorder]
  );

  const handleDragEnd = useCallback(() => {
    finalizeDrag();
  }, [finalizeDrag]);

  const showDropIndicatorBefore = useCallback(
    (index) =>
      isReorderUiActive &&
      isDragging &&
      dragOverIndex === index &&
      dragStartIndex !== null &&
      dragOverIndex !== dragStartIndex &&
      !isDropAfterLast,
    [
      dragOverIndex,
      dragStartIndex,
      isDragging,
      isDropAfterLast,
      isReorderUiActive,
    ]
  );

  if (!items || items.length === 0) {
    return null;
  }

  const showTrailingDropIndicator =
    isReorderUiActive &&
    isDragging &&
    isDropAfterLast &&
    dragStartIndex !== null &&
    dragStartIndex !== itemCount - 1;

  const renderPinnedItem = (item, index) => {
    const {
      avatar,
      name,
      link,
      shape,
      onUnpin,
      gradient,
      type,
      entityType,
      badgeVariant = "default",
      popoverBadge,
      id,
    } = item;

    if (!name || !link) {
      return null;
    }

    const showTeamType = entityType === "team" && Boolean(type);
    const isActive = link && location.pathname.startsWith(link);
    const isItemDragging = dragStartIndex === index;
    const showDragHandle = canReorder && isReorderUiActive;
    const showDropIndicator = showDropIndicatorBefore(index);

    const popoverContent = popoverBadge ? (
      <Flex gap="s" alignItems="center">
        <span>{name}</span>
        {popoverBadge}
      </Flex>
    ) : (
      <span>{name}</span>
    );

    if (isExpanded) {
      return (
        <ReorderableSectionItem
          key={id || name}
          ref={(element) => {
            rowRefs.current[index] = element;
          }}
          data-pinned-row="true"
          isExpanded={isExpanded}
          className={isActive && !isEditMode ? "active" : ""}
          isDragging={isItemDragging}
          isEditMode={isEditMode}
          showDropIndicator={showDropIndicator}
          onDragOver={showDragHandle ? handleDragOver : undefined}
          onDrop={showDragHandle ? handleDrop : undefined}
        >
          {showDragHandle && (
            <DragHandle
              role="button"
              tabIndex={0}
              aria-label={`Reorder ${name}`}
              isDragging={isItemDragging}
              draggable
              onDragStart={(event) => handleDragStart(event, index)}
              onDragEnd={handleDragEnd}
              onKeyDown={(event) => {
                if (event.key === "ArrowUp") {
                  event.preventDefault();
                  handleKeyboardMove(index, -1);
                } else if (event.key === "ArrowDown") {
                  event.preventDefault();
                  handleKeyboardMove(index, 1);
                }
              }}
            >
              <DragHandleGrip aria-hidden="true">
                <DragHandleLine />
                <DragHandleLine />
              </DragHandleGrip>
            </DragHandle>
          )}
          <SectionPinnedLink
            to={link}
            aria-label={name}
            data-pinned-link="true"
            style={isEditMode ? { pointerEvents: "none" } : undefined}
            tabIndex={isEditMode ? -1 : undefined}
            draggable={false}
          >
            <Popover
              text={popoverContent}
              direction="right"
              width="fit-content"
              tabIndex="-1"
              inlineBlock={false}
              style={{ width: "100%", display: "flex", flex: 1 }}
            >
              <SectionPinnedLinkContent>
                <CollapsedAvatarWrapper>
                  <Avatar
                    image={avatar}
                    customSize="24px"
                    shape={shape}
                    gradient={gradient}
                  />
                </CollapsedAvatarWrapper>
                {showTeamType ? (
                  <TeamTypeBar badgeVariant={badgeVariant} />
                ) : (
                  <TeamTypeBarPlaceholder />
                )}
                <SectionItemName>{name}</SectionItemName>
              </SectionPinnedLinkContent>
            </Popover>
          </SectionPinnedLink>
          {onUnpin && !isEditMode && (
            <UnpinButtonWrapper>
              <Popover
                text="Unpin item"
                direction="right"
                width="fit-content"
                tabIndex="-1"
              >
                <Button
                  height="20px"
                  width="20px"
                  borderRadius="20px"
                  variant="borderGrey"
                  borderColor="white"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onUnpin(item);
                  }}
                  type="button"
                >
                  <Icon
                    icon={["fas", "thumbtack"]}
                    color="bodyText"
                    size="sm"
                  />
                </Button>
              </Popover>
            </UnpinButtonWrapper>
          )}
        </ReorderableSectionItem>
      );
    }

    return (
      <SectionItem key={id || name} isExpanded={isExpanded}>
        <Popover
          text={popoverContent}
          direction="right"
          width="fit-content"
          tabIndex="-1"
        >
          <CollapsedItemWrapper className={isActive ? "active" : ""}>
            <CollapsedItemLink to={link} aria-label={name}>
              <CollapsedAvatarWrapper>
                <Avatar
                  image={avatar}
                  customSize="24px"
                  shape={shape}
                  gradient={gradient}
                />
              </CollapsedAvatarWrapper>
              {showTeamType ? (
                <TeamTypeBar badgeVariant={badgeVariant} />
              ) : (
                <TeamTypeBarPlaceholder />
              )}
            </CollapsedItemLink>
          </CollapsedItemWrapper>
        </Popover>
      </SectionItem>
    );
  };

  return (
    <SectionResponsiveContainer
      flexDirection="column"
      gap="r"
      my={isExpanded ? "s" : "0"}
      mb="0"
      pb="0"
    >
      <PinnedDropTarget
        ref={sectionRef}
        onDragOver={
          isReorderUiActive && isDragging ? handleDragOver : undefined
        }
        onDrop={isReorderUiActive && isDragging ? handleDrop : undefined}
      >
        {isExpanded && (
          <PinnedSectionHeader>
            <H6 sizing="small" color="greyDark">
              Pinned
            </H6>
            {canReorder && (
              <Popover
                text={
                  isEditMode ? "Done editing pinned order" : "Edit pinned order"
                }
                direction="right"
                width="fit-content"
                tabIndex="-1"
              >
                <EditModeButton
                  variant={isEditMode ? "ghost" : "subtle"}
                  iconOnly
                  height="20px"
                  width="20px"
                  ariaLabel={
                    isEditMode
                      ? "Done editing pinned order"
                      : "Edit pinned order"
                  }
                  aria-pressed={isEditMode}
                  onClick={toggleEditMode}
                  type="button"
                >
                  <Icon icon={["far", "edit"]} color="greyDark" size="sm" />
                </EditModeButton>
              </Popover>
            )}
          </PinnedSectionHeader>
        )}
        <PinnedSectionItemList
          isExpanded={isExpanded}
          isEditModeDragging={isReorderUiActive && isDragging}
          onDragOver={
            isReorderUiActive && isDragging ? handleDragOver : undefined
          }
          onDrop={isReorderUiActive && isDragging ? handleDrop : undefined}
        >
          {items.map(renderPinnedItem)}
          {isReorderUiActive && (
            <TrailingDropZone
              showDropIndicator={showTrailingDropIndicator}
              onDragOver={handleTrailingDragOver}
              onDrop={handleDrop}
            />
          )}
        </PinnedSectionItemList>
      </PinnedDropTarget>
    </SectionResponsiveContainer>
  );
};

SideNavPinnedSection.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape(PinnedItemShape)),
  isExpanded: PropTypes.bool,
  onReorder: PropTypes.func,
  isEditMode: PropTypes.bool,
  onEditModeChange: PropTypes.func,
};

export default SideNavPinnedSection;
